import { useState, type CSSProperties, type ReactNode } from 'react'
import { Tag } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { PlanDistributionItem, PlanPriceRow } from '@/services/revenue'
import {
    formatShare,
    hasKnownPrices,
    isPaidPlan,
    planMrrOf,
    pricePartsOf,
    summarizePlanDistribution,
    type PricePart,
    type PricePartKind,
} from '@/lib/plan-distribution'
import { PlanPriceAgenciesDialog, type PlanPriceTarget } from './PlanPriceAgenciesDialog'

// Each plan keeps its color whatever its rank. The class sets --plan, which the
// bars, stripes and swatches read. label is the bar label's text color and chip
// its background (the plan color unless that is too light for 4.5:1).
type PlanStyle = { color: string; label: string; chip?: string }

const PLAN_STYLES: Record<string, PlanStyle> = {
    solo: { color: '[--plan:#6366f1]', label: 'text-white', chip: 'bg-[#4f46e5]' },
    solo_yearly: { color: '[--plan:#3730a3] dark:[--plan:#a5b4fc]', label: 'text-white dark:text-zinc-900' },
    agency: { color: '[--plan:#f08a3c]', label: 'text-white', chip: 'bg-[#c2410c]' },
    agency_yearly: { color: '[--plan:#b45309] dark:[--plan:#fdba74]', label: 'text-white dark:text-zinc-900' },
    agency_mini: { color: '[--plan:#0f766e] dark:[--plan:#2dd4bf]', label: 'text-white dark:text-zinc-900' },
    agency_mini_yearly: { color: '[--plan:#134e4a] dark:[--plan:#99f6e4]', label: 'text-white dark:text-zinc-900' },
    free: { color: '[--plan:#a1a1aa] dark:[--plan:#71717a]', label: 'text-white', chip: 'bg-[#71717a]' },
    not_selected: { color: '[--plan:#e4e4e7] dark:[--plan:#3f3f46]', label: 'text-zinc-700 dark:text-zinc-200' },
}
const OTHER_STYLE: PlanStyle = {
    color: '[--plan:#8b5cf6] dark:[--plan:#a78bfa]',
    label: 'text-white dark:text-zinc-900',
    chip: 'bg-[#7c3aed] dark:bg-[var(--plan)]',
}

const styleOf = (item: PlanDistributionItem) => PLAN_STYLES[item.planName] ?? OTHER_STYLE

const PART_STYLES: Record<PricePartKind, CSSProperties> = {
    current: { backgroundColor: 'var(--plan)' },
    solid: { backgroundColor: 'var(--plan)' },
    older: {
        backgroundColor: 'color-mix(in oklch, var(--plan) 22%, transparent)',
        backgroundImage: 'repeating-linear-gradient(135deg, var(--plan) 0 2px, transparent 2px 5px)',
    },
    unknown: { backgroundColor: 'color-mix(in oklch, var(--plan) 40%, transparent)' },
}

const PART_LABELS: Record<PricePartKind, string> = {
    current: 'Current price',
    older: 'Older prices',
    unknown: 'Price not on record',
    solid: '',
}

// Phone-width cards keep Plan, Agencies and MRR; the share columns join from @md
// (the page's two-column layout at 1440) and get their full headers from @xl.
const ROW_GRID =
    'grid grid-cols-[minmax(0,1fr)_3.5rem_4.25rem] items-center gap-x-2 @md:grid-cols-[minmax(0,1fr)_3.5rem_3rem_5rem_3rem] @md:gap-x-3 @xl:grid-cols-[minmax(0,1fr)_4rem_5.5rem_5.5rem_4rem]'

const formatMoney = (value: number) => `$${Math.round(value).toLocaleString('en-US')}`

const formatPrice = (amount: number) =>
    `$${amount.toLocaleString('en-US', {
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
    })}/mo`

const countOf = (count: number, one: string, many: string) =>
    `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`

const priceKindOf = (price: PlanPriceRow): PricePartKind =>
    price.monthlyAmount === null ? 'unknown' : price.isCurrent ? 'current' : 'older'

function Swatch({ item, kind = 'solid', small = false }: { item: PlanDistributionItem; kind?: PricePartKind; small?: boolean }) {
    return (
        <span
            className={cn('shrink-0', small ? 'h-2 w-2 rounded-[2px]' : 'h-2.5 w-2.5 rounded-[3px]', styleOf(item).color)}
            style={PART_STYLES[kind]}
        />
    )
}

function ShareBar({
    title,
    totalLabel,
    items,
    metric,
}: {
    title: string
    totalLabel: string
    items: PlanDistributionItem[]
    metric: 'agencies' | 'mrr'
}) {
    const segments = items
        .map((item) => ({
            item,
            weight: metric === 'agencies' ? item.agencyCount : planMrrOf(item),
            parts: pricePartsOf(item).filter((part) => part[metric] > 0),
        }))
        .filter((segment) => segment.weight > 0 && segment.parts.length > 0)
    const total = segments.reduce((sum, segment) => sum + segment.weight, 0)

    const partValue = (part: PricePart) =>
        metric === 'agencies' ? countOf(part.agencies, 'agency', 'agencies') : `${formatMoney(part.mrr)} / month`

    return (
        <div className="space-y-2">
            <div className="flex items-baseline justify-between gap-3 text-xs">
                <span className="font-medium text-muted-foreground">{title}</span>
                <span className="text-muted-foreground tabular-nums">{totalLabel}</span>
            </div>
            <div className="flex h-7 gap-0.5 overflow-hidden rounded-md">
                {segments.map(({ item, weight, parts }) => (
                    <div
                        key={item.planName}
                        className={cn('relative flex min-w-[3px] gap-px', styleOf(item).color)}
                        style={{ flex: `${weight} 1 0%` }}
                    >
                        {parts.map((part) => (
                            <Tooltip key={part.kind}>
                                <TooltipTrigger asChild>
                                    <div
                                        className="h-full min-w-[2px]"
                                        style={{ flex: `${part[metric]} 1 0%`, ...PART_STYLES[part.kind] }}
                                    />
                                </TooltipTrigger>
                                <TooltipContent className="font-normal">
                                    <div className="font-medium">
                                        {item.displayName}
                                        {PART_LABELS[part.kind] && `: ${PART_LABELS[part.kind].toLowerCase()}`}
                                    </div>
                                    <div className="tabular-nums">
                                        {partValue(part)} ({formatShare(part[metric], total)})
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        ))}
                        {weight / total >= 0.08 && (
                            <span
                                className={cn(
                                    'pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 rounded-sm px-1 text-[11px] font-semibold leading-4 tabular-nums',
                                    // A phone-width bar has no room for a label on a thin segment.
                                    weight / total < 0.12 && 'hidden @md:block',
                                    styleOf(item).chip ?? 'bg-[var(--plan)]',
                                    styleOf(item).label,
                                )}
                            >
                                {formatShare(weight, total)}
                            </span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

function GroupRow({ label, agencies, totalAgencies, mrr, totalMrr }: {
    label: string
    agencies: number
    totalAgencies: number
    mrr: number
    totalMrr: number
}) {
    return (
        <div role="row" className={cn(ROW_GRID, 'pt-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground tabular-nums')}>
            <span role="rowheader">{label}</span>
            <span role="cell" className="text-right">{agencies.toLocaleString('en-US')}</span>
            <span role="cell" className="hidden text-right @md:block">{formatShare(agencies, totalAgencies)}</span>
            <span role="cell" className="text-right">{formatMoney(mrr)}</span>
            <span role="cell" className="hidden text-right @md:block">{formatShare(mrr, totalMrr)}</span>
        </div>
    )
}

function PlanRows({ item, totalAgencies, totalMrr, onSelectPrice }: {
    item: PlanDistributionItem
    totalAgencies: number
    totalMrr: number
    onSelectPrice: (target: PlanPriceTarget) => void
}) {
    // Price rows show even when every price is unknown, so each row can open its agencies.
    const showPrices = isPaidPlan(item) && item.prices.length > 0
    const mrr = planMrrOf(item)
    return (
        <div role="rowgroup" className="border-t border-border/60 py-1 first:border-t-0">
            <div role="row" className={cn(ROW_GRID, 'min-h-9 text-sm tabular-nums')}>
                <span role="rowheader" className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 font-medium">
                    <span className="flex min-w-0 items-center gap-2.5">
                        <Swatch item={item} />
                        <span className="truncate">{item.displayName}</span>
                    </span>
                    {isPaidPlan(item) && !showPrices && item.listMonthlyPrice !== null && item.listMonthlyPrice > 0 && (
                        <span className="text-xs font-normal text-muted-foreground">{formatPrice(item.listMonthlyPrice)}</span>
                    )}
                </span>
                <span role="cell" className="text-right font-semibold">{item.agencyCount.toLocaleString('en-US')}</span>
                <span role="cell" className="hidden text-right text-muted-foreground @md:block">{formatShare(item.agencyCount, totalAgencies)}</span>
                <span role="cell" className={cn('text-right', mrr > 0 ? 'font-semibold' : 'text-muted-foreground')}>
                    {formatMoney(mrr)}
                </span>
                <span role="cell" className="hidden text-right text-muted-foreground @md:block">{formatShare(mrr, totalMrr)}</span>
            </div>
            {showPrices &&
                item.prices.map((price) => {
                    // A plan with no known price draws a solid bar, so its swatch matches.
                    const kind = hasKnownPrices(item) ? priceKindOf(price) : 'solid'
                    const priceLabel = price.monthlyAmount === null ? 'Price not on record' : formatPrice(price.monthlyAmount)
                    return (
                        <div key={price.monthlyAmount ?? 'unknown'} role="row" className={cn(ROW_GRID, 'relative min-h-7 rounded-sm text-xs tabular-nums hover:bg-muted/60')}>
                            <span role="rowheader" className="min-w-0 pl-3 @md:pl-5">
                                {/* The button's ::after covers the whole row, so any part of it opens the list. */}
                                <button
                                    type="button"
                                    aria-haspopup="dialog"
                                    onClick={() =>
                                        item.planId !== null &&
                                        onSelectPrice({ planId: item.planId, monthlyAmount: price.monthlyAmount, title: `${item.displayName} · ${priceLabel}` })
                                    }
                                    className="flex min-w-0 cursor-pointer flex-wrap items-center gap-x-1.5 gap-y-0.5 text-left text-muted-foreground after:absolute after:inset-0 after:rounded-sm focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring @md:gap-x-2"
                                >
                                    <Swatch item={item} kind={kind} small />
                                    {price.monthlyAmount === null ? (
                                        <span className="font-medium text-foreground">Price not on record</span>
                                    ) : (
                                        <>
                                            <span className="font-semibold text-foreground">{formatPrice(price.monthlyAmount)}</span>
                                            {kind === 'current' ? (
                                                <span className="rounded-full bg-indigo-50 px-1.5 py-px text-[11px] font-semibold text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300">
                                                    Current
                                                </span>
                                            ) : (
                                                <span className="rounded-full bg-muted px-1.5 py-px text-[11px] font-semibold text-muted-foreground">
                                                    Older
                                                </span>
                                            )}
                                        </>
                                    )}
                                    <span className="hidden @xl:inline">
                                        {formatShare(price.agencyCount, item.agencyCount)} of plan
                                    </span>
                                </button>
                            </span>
                            <span role="cell" className="text-right text-foreground">{price.agencyCount.toLocaleString('en-US')}</span>
                            <span role="cell" className="hidden @md:block" />
                            <span role="cell" className="text-right text-foreground">{formatMoney(price.revenue)}</span>
                            <span role="cell" className="hidden @md:block" />
                        </div>
                    )
                })}
        </div>
    )
}

function LegendItem({ style, children }: { style: CSSProperties; children: ReactNode }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className={cn('h-2.5 w-3.5 rounded-[2px]', PLAN_STYLES.solo.color)} style={style} />
            {children}
        </span>
    )
}

export function PlanDistributionCard({ items }: { items: PlanDistributionItem[] }) {
    const visible = items.filter((item) => item.agencyCount > 0)
    const paid = visible.filter(isPaidPlan)
    const unpaid = visible.filter((item) => !isPaidPlan(item))
    // Bars read in the table's order: paid plans first, then Free and Not selected.
    const barOrder = [...paid, ...unpaid]
    const summary = summarizePlanDistribution(visible)
    const [selectedPrice, setSelectedPrice] = useState<PlanPriceTarget | null>(null)
    const showPriceLegend = visible.some(hasKnownPrices)
    const hasUnknownPrices = visible.some((item) => hasKnownPrices(item) && item.prices.some((price) => price.monthlyAmount === null))
    const sum = (rows: PlanDistributionItem[], pick: (item: PlanDistributionItem) => number) =>
        rows.reduce((total, item) => total + pick(item), 0)
    const oneOlder = summary.olderPriceAgencies === 1

    return (
        <Card className="py-0 gap-0">
            <CardHeader className="py-6 gap-0 border-b">
                <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
                    <div className="space-y-1.5">
                        <CardTitle className="text-lg font-semibold">Plan Distribution</CardTitle>
                        <CardDescription>Where agencies sit, and what price they pay</CardDescription>
                    </div>
                    {summary.totalAgencies > 0 && (
                        <div className="ml-auto text-right tabular-nums">
                            <div className="text-2xl font-bold leading-none">
                                {summary.paidPlanAgencies.toLocaleString('en-US')}{' '}
                                <span className="text-sm font-medium text-muted-foreground">on paid plans</span>
                            </div>
                            <div className="mt-1.5 text-xs text-muted-foreground">
                                {formatShare(summary.paidPlanAgencies, summary.totalAgencies)} of{' '}
                                {countOf(summary.totalAgencies, 'agency', 'agencies')}
                            </div>
                        </div>
                    )}
                </div>
            </CardHeader>
            <CardContent className="@container pt-6 pb-6">
                {visible.length > 0 ? (
                    <div className="space-y-5">
                        {/* The table below carries every number, so screen readers skip the bars. */}
                        <div className="space-y-4" aria-hidden="true">
                            <ShareBar
                                title="Share of agencies"
                                totalLabel={countOf(summary.totalAgencies, 'agency', 'agencies')}
                                items={barOrder}
                                metric="agencies"
                            />
                            {summary.totalMrr > 0 && (
                                <ShareBar
                                    title="Share of MRR"
                                    totalLabel={`${formatMoney(summary.totalMrr)} / month`}
                                    items={barOrder}
                                    metric="mrr"
                                />
                            )}
                            {showPriceLegend && (
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                    <LegendItem style={PART_STYLES.current}>Current price</LegendItem>
                                    <LegendItem style={PART_STYLES.older}>Older price</LegendItem>
                                    {hasUnknownPrices && <LegendItem style={PART_STYLES.unknown}>Price not on record</LegendItem>}
                                </div>
                            )}
                        </div>

                        {summary.olderPriceAgencies > 0 && (
                            <div className="flex items-start gap-3 rounded-lg border bg-muted/40 px-3.5 py-3 text-sm">
                                <Tag className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" />
                                <p className="leading-snug">
                                    <span className="font-semibold">
                                        {summary.olderPriceAgencies.toLocaleString('en-US')} of{' '}
                                        {countOf(summary.payingAgencies, 'paying agency', 'paying agencies')} (
                                        {formatShare(summary.olderPriceAgencies, summary.payingAgencies)})
                                    </span>{' '}
                                    <span className="text-muted-foreground">{oneOlder ? 'is' : 'are'} on an older price.</span>
                                    {/* Under 50 cents would read "$0/mo more". */}
                                    {summary.olderPriceMonthlyGap >= 0.5 && (
                                        <>
                                            {' '}
                                            <span className="text-muted-foreground">
                                                {oneOlder ? "At today's price it would pay" : "At today's prices they would pay"}
                                            </span>{' '}
                                            <span className="font-semibold">{formatMoney(summary.olderPriceMonthlyGap)}/mo more</span>
                                            <span className="text-muted-foreground">.</span>
                                        </>
                                    )}
                                </p>
                            </div>
                        )}

                        <div role="table" aria-label="Agencies and MRR by plan and price">
                            <div role="row" className={cn(ROW_GRID, 'border-b pb-2 text-xs text-muted-foreground')}>
                                <span role="columnheader">Plan and price</span>
                                <span role="columnheader" className="text-right">Agencies</span>
                                <span role="columnheader" className="hidden text-right @md:block">
                                    %<span className="sr-only @xl:not-sr-only"> agencies</span>
                                </span>
                                <span role="columnheader" className="text-right">MRR</span>
                                <span role="columnheader" className="hidden text-right @md:block">
                                    %<span className="sr-only @xl:not-sr-only"> MRR</span>
                                </span>
                            </div>
                            {paid.length > 0 && (
                                <>
                                    <GroupRow
                                        label="Paid plans"
                                        agencies={summary.paidPlanAgencies}
                                        totalAgencies={summary.totalAgencies}
                                        mrr={sum(paid, planMrrOf)}
                                        totalMrr={summary.totalMrr}
                                    />
                                    <div role="none">
                                        {paid.map((item) => (
                                            <PlanRows key={item.planName} item={item} totalAgencies={summary.totalAgencies} totalMrr={summary.totalMrr} onSelectPrice={setSelectedPrice} />
                                        ))}
                                    </div>
                                </>
                            )}
                            {unpaid.length > 0 && (
                                <div role="none" className={cn(paid.length > 0 && 'mt-1 border-t')}>
                                    <GroupRow
                                        label="No paid plan"
                                        agencies={sum(unpaid, (item) => item.agencyCount)}
                                        totalAgencies={summary.totalAgencies}
                                        mrr={sum(unpaid, planMrrOf)}
                                        totalMrr={summary.totalMrr}
                                    />
                                    <div role="none">
                                        {unpaid.map((item) => (
                                            <PlanRows key={item.planName} item={item} totalAgencies={summary.totalAgencies} totalMrr={summary.totalMrr} onSelectPrice={setSelectedPrice} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                        No plan data available
                    </div>
                )}
            </CardContent>
            <PlanPriceAgenciesDialog target={selectedPrice} onClose={() => setSelectedPrice(null)} />
        </Card>
    )
}
